import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  SONG_CAMERA_STYLE_OPTIONS,
  SONG_CHARACTER_AGE_OPTIONS,
  SONG_CHARACTER_GENDER_OPTIONS,
  SONG_CLOTHING_OPTIONS,
  SONG_COLOR_PALETTE_OPTIONS,
  SONG_CONSISTENCY_OPTIONS,
  SONG_COUNTRY_REGION_OPTIONS,
  SONG_ENVIRONMENT_OPTIONS,
  SONG_ETHNICITY_OPTIONS,
  SONG_LENS_STYLE_OPTIONS,
  SONG_LIGHTING_OPTIONS,
  SONG_MOOD_OPTIONS,
  SONG_TIME_OF_DAY_OPTIONS,
} from '@/lib/songBasedProductionMetadata';

function MetaSelect({ label, value, onChange, options, placeholder }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder={placeholder || 'Choose…'} /></SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function MetaInput({ label, value, onChange, placeholder }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

export function SongBasedProductionMetadataForm({ metadata, onChange }) {
  const update = (key, val) => onChange({ ...metadata, [key]: val });

  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Creative direction</p>

      <div className="grid sm:grid-cols-2 gap-3">
        <MetaSelect label="Environment" value={metadata.environment} onChange={(v) => update('environment', v)} options={SONG_ENVIRONMENT_OPTIONS} />
        <MetaInput label="Background" value={metadata.background} onChange={(v) => update('background', v)} placeholder="e.g. monsoon clouds, neon skyline" />
        <MetaSelect label="Country / region" value={metadata.country_region} onChange={(v) => update('country_region', v)} options={SONG_COUNTRY_REGION_OPTIONS} />
        <MetaInput label="Actors origin" value={metadata.actors_origin} onChange={(v) => update('actors_origin', v)} placeholder="e.g. South Indian coastal" />
        <MetaSelect label="Ethnicity" value={metadata.ethnicity} onChange={(v) => update('ethnicity', v)} options={SONG_ETHNICITY_OPTIONS} />
        <MetaSelect label="Time of day / weather" value={metadata.time_of_day} onChange={(v) => update('time_of_day', v)} options={SONG_TIME_OF_DAY_OPTIONS} />
        <MetaSelect label="Clothing" value={metadata.clothing_style} onChange={(v) => update('clothing_style', v)} options={SONG_CLOTHING_OPTIONS} />
        <MetaSelect label="Color palette" value={metadata.color_palette} onChange={(v) => update('color_palette', v)} options={SONG_COLOR_PALETTE_OPTIONS} />
        <MetaSelect label="Camera style" value={metadata.camera_style} onChange={(v) => update('camera_style', v)} options={SONG_CAMERA_STYLE_OPTIONS} />
        <MetaSelect label="Lens" value={metadata.lens_style} onChange={(v) => update('lens_style', v)} options={SONG_LENS_STYLE_OPTIONS} />
        <MetaSelect label="Lighting" value={metadata.lighting} onChange={(v) => update('lighting', v)} options={SONG_LIGHTING_OPTIONS} />
        <MetaSelect label="Mood" value={metadata.mood} onChange={(v) => update('mood', v)} options={SONG_MOOD_OPTIONS} />
      </div>

      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-1">Character appearance</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <MetaSelect label="Age" value={metadata.character_age} onChange={(v) => update('character_age', v)} options={SONG_CHARACTER_AGE_OPTIONS} />
        <MetaSelect label="Gender" value={metadata.character_gender} onChange={(v) => update('character_gender', v)} options={SONG_CHARACTER_GENDER_OPTIONS} />
        <MetaInput label="Body type" value={metadata.character_body_type} onChange={(v) => update('character_body_type', v)} placeholder="e.g. athletic, slender" />
        <MetaInput label="Height" value={metadata.character_height} onChange={(v) => update('character_height', v)} placeholder="e.g. tall, average" />
        <MetaInput label="Face shape" value={metadata.character_face_shape} onChange={(v) => update('character_face_shape', v)} />
        <MetaInput label="Hair style" value={metadata.character_hair_style} onChange={(v) => update('character_hair_style', v)} />
        <MetaInput label="Hair color" value={metadata.character_hair_color} onChange={(v) => update('character_hair_color', v)} />
        <MetaInput label="Beard" value={metadata.character_beard} onChange={(v) => update('character_beard', v)} />
        <MetaInput label="Accessories" value={metadata.character_accessories} onChange={(v) => update('character_accessories', v)} />
        <MetaInput label="Jewelry" value={metadata.character_jewelry} onChange={(v) => update('character_jewelry', v)} />
        <MetaInput label="Tattoos" value={metadata.character_tattoos} onChange={(v) => update('character_tattoos', v)} />
        <MetaInput label="Glasses" value={metadata.character_glasses} onChange={(v) => update('character_glasses', v)} />
      </div>

      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-1">Image model preferences</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <MetaInput label="Negative prompt" value={metadata.negative_prompt} onChange={(v) => update('negative_prompt', v)} placeholder="blur, watermark, text" />
        <MetaSelect label="Consistency strength" value={metadata.consistency_strength} onChange={(v) => update('consistency_strength', v)} options={SONG_CONSISTENCY_OPTIONS} />
        <MetaInput label="Character lock" value={metadata.character_lock} onChange={(v) => update('character_lock', v)} placeholder="e.g. same lead in every scene" />
        <MetaInput label="Seed lock" value={metadata.seed_lock} onChange={(v) => update('seed_lock', v)} placeholder="optional seed" />
        <MetaInput label="Style reference" value={metadata.style_reference} onChange={(v) => update('style_reference', v)} placeholder="reference film or art style" />
        <MetaInput label="Reference images" value={metadata.reference_images_notes} onChange={(v) => update('reference_images_notes', v)} placeholder="notes for uploaded refs" />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Extra notes</Label>
        <Textarea value={metadata.extra_notes || ''} onChange={(e) => update('extra_notes', e.target.value)} rows={2} placeholder="Any other visual direction" />
      </div>
    </div>
  );
}

function SongWaveformPreview({ peaks }) {
  if (!peaks?.length) return null;
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-xs font-medium text-slate-500 mb-2">Waveform preview</p>
      <div className="flex items-end gap-px h-12 w-full overflow-hidden">
        {peaks.map((p, i) => (
          <div
            key={i}
            className="flex-1 min-w-0 bg-pink-400/80 rounded-t-sm"
            style={{ height: `${Math.max(8, p * 100)}%` }}
          />
        ))}
      </div>
    </div>
  );
}

export { SongWaveformPreview };
